import { Form } from 'antd';
import { FC, memo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { SiteGroupFormFields, SiteGroupFormInputs, SiteGroupsQueryKeys, useCreateSiteGroupsMutation } from '@/modules/SiteGroups';

import { SiteGroupsFormView } from './SiteGroupsFormView';
import { siteGroupsFormValidationSchema } from './validation';

export const SiteGroupsCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<SiteGroupFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof SiteGroupFormInputs>>(null);

  const { mutate: createSiteGroupMutation, isLoading: createSiteGroupIsLoading } = useCreateSiteGroupsMutation()({
    onSuccess: async (data) => {
      appMessageApi.success('Групу сайтів успішно створено!');

      await queryClient.refetchQueries([SiteGroupsQueryKeys.GetAll]);

      navigate(AppRoutes.SiteGroupsById.replace(':siteGroupId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SiteGroupFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: SiteGroupFormFields = {
    name: undefined,
    siteIds: []
  };

  const handleFinish = (values: SiteGroupFormFields): void => {
    setFormErrors(null);
    createSiteGroupMutation(values);
  };

  const handleBack = (): void => {
    navigate(AppRoutes.SiteGroups);
  };

  const handleFinishFailed = (): void => {
    setFormErrors(null);
  };

  return (
    <SiteGroupsFormView
      formName="site-group-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createSiteGroupIsLoading }
      validationSchema={ siteGroupsFormValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
